import { BORNES_BULLE, BORNES_RECHERCHE } from '@nextilt/commun';
import type { NoteDeLaBoite, ReponseBoite } from '../partage/api/types';

/**
 * Les règles pures de la page Boîte d'entrée (chantier boite-d-entree, ticket 02), sans rendu :
 * ce qui part quand on note, la lecture tenue après un geste, le compte de la barre de gauche et
 * les choix de « Ranger dans… ». L'ordre des notes vient du serveur (la plus récente d'abord) ;
 * l'écran ne le recalcule pas, il pose en haut la note qu'il vient de noter.
 */

/**
 * Ce que la case « Noter… » envoie : le texte sans ses blancs autour, coupé à la borne d'un titre
 * de bulle (rangée, la note en devient un) ; rien d'écrit, rien ne part.
 */
export function texteANoter(ecrit: string): string | null {
  const texte = ecrit.trim().slice(0, BORNES_BULLE.titreMax).trim();
  return texte === '' ? null : texte;
}

/** La note notée, en haut ; déjà là, elle n'est pas doublée. */
export function avecLaNote(lu: ReponseBoite, note: NoteDeLaBoite): ReponseBoite {
  return { ...lu, notes: [note, ...lu.notes.filter((n) => n.id !== note.id)] };
}

/** La note retirée ou rangée part ; les autres gardent leur ordre. */
export function sansLaNote(lu: ReponseBoite, id: string): ReponseBoite {
  return { ...lu, notes: lu.notes.filter((n) => n.id !== id) };
}

/** Le nombre à côté de « Boîte d'entrée » dans la barre ; rien quand elle est vide ou pas encore lue. */
export function compteDeLaBarre(lu: ReponseBoite | undefined): number | null {
  const n = lu?.notes.length ?? 0;
  return n > 0 ? n : null;
}

/** « Ranger dans… » cherche dès deux signes, comme la recherche de Lier (`BORNES_RECHERCHE`). */
export function chercheParTitre(texte: string): boolean {
  return texte.trim().length >= BORNES_RECHERCHE.signesMin;
}

/** Un choix de « Ranger dans… » : la bulle, et où elle vit (vide pour un projet). */
export interface ChoixDeRangement {
  id: string;
  titre: string;
  ou: string[];
}

/**
 * Les choix de « Ranger dans… » : rien de cherché, les projets ; un mot cherché, les bulles
 * trouvées par le serveur dans tous les projets, avec leur chemin pour départager les homonymes.
 */
export function choixDeRangement(
  texte: string,
  projets: readonly { id: string; titre: string }[],
  resultats: readonly { id: string; titre: string; chemin: string[] }[]
): ChoixDeRangement[] {
  if (!chercheParTitre(texte)) return projets.map((p) => ({ id: p.id, titre: p.titre, ou: [] }));
  return resultats.map((r) => ({ id: r.id, titre: r.titre, ou: r.chemin }));
}
